import { SerializedStyles } from '@emotion/react';
import React from 'react';

import BaseCardView from './BaseCardView';
import TextWithSymbols, { PerAgePillOptions } from './TextWithSymbols';

import { css } from '@emotion/react';
import { TributeDef } from '@mythos/game/GameModel';
import { getTributeLogic, TributeLogic } from '@mythos/game/TributeLogic';
import TributeImageBaeren from '../assets/tribute_card/TributeArtBaeren.jpg';
import TributeImageDerion from '../assets/tribute_card/TributeArtDerionCropped.jpg';
import TributeImageTyros from '../assets/tribute_card/TributeArtTyrosCropped.jpg';
import TributeImageZeona from '../assets/tribute_card/TributeArtZeonaCropped.jpg';
import { ColorPalette } from './ColorPalette';

import TributeFrame from '../assets/card_frame/frame_tribute.svg';
import TributeFrameMini from '../assets/card_frame/frame_tribute_mini.svg';
import TributeTitleDecoration from '../assets/card_frame/title_tribute_decoration.svg';
import { ArtResolutionContext, tributeArtUrl } from './ArtResolution';

type TributeCardEventHandler<TCard> = (
  card: TCard,
  event: React.MouseEvent,
) => void;

export type TributeCardRenderer<TCard extends TributeDef> = (
  props: Props<TCard>,
) => React.ReactElement;

type Props<TCard extends TributeDef = TributeDef> = {
  card: TCard;

  onCardEnter?: TributeCardEventHandler<TCard>;
  onCardLeave?: TributeCardEventHandler<TCard>;
  onClick?: TributeCardEventHandler<TCard>;

  overlayStyle?: React.CSSProperties;
  overlayCSS?: SerializedStyles;
  overlayChildren?: React.ReactNode;
} & (
  | {
      mini: true;
      currentAge: number;
    }
  | {
      mini?: false;
      currentAge?: number;
    }
);

const TributeCardView = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    let { card, currentAge, mini } = props;
    const handleMouseEnter = (e: React.MouseEvent) => {
      props.onCardEnter && props.onCardEnter(card, e);
    };

    const handleMouseLeave = (e: React.MouseEvent) => {
      props.onCardLeave && props.onCardLeave(card, e);
    };

    const handleClick = (e: React.MouseEvent) => {
      props.onClick && props.onClick(card, e);
    };

    const tributeLogic = getTributeLogic(card);

    const artResolution = React.useContext(ArtResolutionContext);
    const art = card.imageName && tributeArtUrl(card.imageName, artResolution);

    // Radial vignette over the art: transparent through the medallion,
    // easing to 35% black at the corners (smoothstep-ish stops).
    const vignette =
      'radial-gradient(ellipse farthest-corner at center, ' +
      'rgba(0,0,0,0) 53%, rgba(0,0,0,0.06) 65%, rgba(0,0,0,0.18) 76%, ' +
      'rgba(0,0,0,0.3) 88%, rgba(0,0,0,0.35) 100%)';

    let card_style: React.CSSProperties = {
      backgroundImage: (art && `${vignette}, url(${art})`) || undefined,
      // art is combo-piece crops at the rendered card aspect (1038:744),
      // so cover maps 1:1 with no trim
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'rgb(24, 20, 24)',
    };

    let title_view = (
      <div css={TributeStyle.titleContainer}>
        <div css={TributeStyle.nameContainer}>
          <svg css={TributeStyle.titleDecoration} preserveAspectRatio="none">
            <image
              href={TributeTitleDecoration}
              width="100%"
              height="100%"
              preserveAspectRatio="none"
            />
          </svg>
          <div css={TributeStyle.name}>{card.name.toUpperCase()}</div>
          <svg
            css={TributeStyle.titleDecoration}
            style={{ transform: 'scaleX(-1)' }}
            preserveAspectRatio="none"
          >
            <image
              href={TributeTitleDecoration}
              width="100%"
              height="100%"
              preserveAspectRatio="none"
            />
          </svg>
        </div>
        <div css={TributeStyle.subtitle}>{card.subtitle.toUpperCase()}</div>
      </div>
    );

    const thresholds = tributeLogic?.thresholds;
    const description = tributeLogic?.description();

    return (
      <BaseCardView
        className={'tribute'}
        ref={ref}
        cardStyle={card_style}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onLeftClick={handleClick}
        overlayChildren={props.overlayChildren}
        overlayCSS={props.overlayCSS}
        overlayStyle={props.overlayStyle}
        landscape={true}
        mini={mini}
      >
        <div css={TributeStyle.frame}>
          {title_view}
          <div css={TributeStyle.effect_container}>
            {description && (
              <TextWithSymbols
                css={TributeStyle.effect}
                content={description.toUpperCase()}
                perAgePillOptions={
                  thresholds
                    ? {
                        display: 'all-values',
                        values: thresholds,
                        currentAge,
                      }
                    : undefined
                }
                symbolStyle={TributeStyle.effect_symbol}
              />
            )}
          </div>
        </div>
      </BaseCardView>
    );
  },
);

const MiniTributeContent = (props: {
  card: TributeDef;
  tributeLogic: TributeLogic;
  currentAge: number;
}) => {
  const { tributeLogic, card, currentAge } = props;
  const thresholds = tributeLogic?.thresholds;
  const perAgePillOptions: PerAgePillOptions | undefined = thresholds
    ? currentAge
      ? {
          display: 'single-value',
          values: thresholds,
          currentAge,
        }
      : {
          display: 'all-values',
          values: thresholds,
          currentAge,
        }
    : undefined;

  return (
    <div css={TributeStyle.body}>
      <div css={TributeStyle.mini_effect_container}>
        <TextWithSymbols
          css={TributeStyle.mini_effect}
          content={tributeLogic.description()}
          perAgePillOptions={perAgePillOptions}
        />
      </div>
    </div>
  );
};

export default TributeCardView;

export const BasicTributeRenderer = (props: Props) => {
  return <TributeCardView {...props} />;
};
export const MakeHighlightedAgeTributeRenderer = (age: number) => {
  return (props: Props) => {
    return BasicTributeRenderer({ ...props, currentAge: age });
  };
};

export const TRIBUTE_TYPE_TO_IMAGE: Record<string, string> = {
  Baeren: TributeImageBaeren,
  Derion: TributeImageDerion,
  Zeona: TributeImageZeona,
  Tyros: TributeImageTyros,
};
const TributeStyle = {
  titleContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
    color: ColorPalette.brightText,
    fontSize: 7,
    height: 14,
    width: 81,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 2,
  }),
  nameContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  }),
  titleDecoration: css({
    display: 'block',
    marginTop: -1,
    flex: 1,
    minWidth: 0,
    height: 0.5,
    overflow: 'hidden',
    padding: 3,
  }),
  name: css({
    fontFamily: 'Mythos-CardTitle',

    fontSize: 7,
    height: 8,
  }),
  subtitle: css({
    fontFamily: 'Mythos-CardTitle',

    fontSize: 3.5,
    height: 6,
    paddingTop: 1,
  }),
  body: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    position: 'relative',
    flexGrow: 1,
  }),
  frame: css({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: `url(${TributeFrame})`,
    '.mini &': {
      backgroundImage: `url(${TributeFrameMini})`,
    },
    backgroundSize: '100% 100%',
  }),
  effect_container: css({
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    width: 80,
    height: 13,
    marginLeft: 'auto',
    marginRight: 'auto',
    left: 0,
    right: 0,
  }),
  effect: css({
    fontSize: 5,
    fontFamily: 'Mythos-CardTitle',
    color: 'rgb(211, 211, 211)',
    textAlign: 'center',
    marginTop: 'auto',
    marginBottom: 'auto',
    fontWeight: 400,
    lineHeight: 1.2,
  }),
  effect_symbol: css({
    height: 6,
    width: 'auto',
    verticalAlign: 'middle',
    marginBottom: 1,
    marginRight: -1,
  }),
  mini_effect_container: css({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    verticalAlign: 'top',
    position: 'relative',
  }),
  mini_effect: css({
    fontSize: 9.5,
    color: 'rgb(211, 211, 211)',
    textAlign: 'center',
    padding: 2,
    marginTop: 'auto',
    marginBottom: 'auto',
    fontWeight: 400,
    lineHeight: 1.3,
  }),
  mini_favor_container: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 5,
  }),
} as const;
